import React, { useState, useEffect, useRef } from 'react';
import {
  ShieldAlert,
  TrendingUp,
  Zap,
  RefreshCw,
  Cpu,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  ChevronDown,
  Layers,
  FileCode2,
  CheckCircle2,
  XCircle,
  Check,
  Clock,
  Activity
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import { automationApi } from '../../../services/automationapi';
import { useClientLevel } from '../../../hooks/useClientLevel';

interface TrendsSummaryData {
  range?: string;
  range_label?: string;
  window_days?: number;
  from?: string;
  to?: string;
  scope?: {
    level?: string;
    client_id?: number | string;
  };
  rules?: {
    total?: number;
    active?: number;
    inactive?: number;
    default?: number;
    custom?: number;
    broken?: number;
  };
  alerts_now?: {
    open?: number;
    acknowledged?: number;
    resolved?: number;
  };
  alerts_opened?: {
    current?: number;
    previous?: number;
    delta_pct?: number | null;
  };
  alerts_resolved?: {
    current?: number;
    previous?: number;
    delta_pct?: number | null;
  };
  actions?: {
    current?: number;
    previous?: number;
    failed_current?: number;
    failed_previous?: number;
    success_rate_current?: number;
    success_rate_previous?: number | null;
  };
}

interface TrendMetricData {
  metric: string;
  range?: string;
  range_label?: string;
  interval?: string;
  from?: string;
  to?: string;
  keys: string[];
  series: Array<{
    bucket: string;
    [key: string]: any;
  }>;
  totals?: Record<string, number>;
}

interface LeaderboardSparklinePoint {
  t: string;
  v: number;
}

interface LeaderboardItem {
  id: number | string;
  name: string;
  count: number;
  sparkline: LeaderboardSparklinePoint[];
}

interface LeaderboardResponseData {
  kind: string;
  range?: string;
  range_label?: string;
  window_days?: number;
  from?: string;
  to?: string;
  sparkline_interval?: string;
  sparkline_buckets?: string[];
  scope?: any;
  filter?: any;
  items: LeaderboardItem[];
}

const RANGE_OPTIONS = [
  { value: 'last_24_hours', label: 'Last 24 Hours' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom' },
];

const KIND_OPTIONS = [
  { value: 'rules', label: 'Rules' },
  { value: 'clients', label: 'Clients' },
  { value: 'assets', label: 'Assets' },
  { value: 'collections', label: 'Collections' },
];

const TREND_METRIC_OPTIONS = [
  { value: 'alerts_lifecycle', label: 'Alerts Lifecycle' },
  { value: 'alerts_by_status', label: 'Alerts By Status' },
  { value: 'alerts_by_severity', label: 'Alerts By Severity' },
  { value: 'alerts_by_collection', label: 'Alerts By Collection' },
  { value: 'new_vs_recurring', label: 'New Vs Recurring' },
  { value: 'open_backlog', label: 'Open Backlog' },
  { value: 'default_vs_custom', label: 'Default Vs Custom' },
  { value: 'global_vs_client', label: 'Global Vs Client' },
  { value: 'alerts_by_client', label: 'Alerts By Client' },
  { value: 'rules_created', label: 'Rules Created' },
  { value: 'action_success_rate', label: 'Action Success Rate' },
];

const COLOR_PALETTE = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // rose
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#f97316', // orange
  '#6366f1', // indigo
];

const getKeyColor = (key: string, index: number): string => {
  const lower = key.toLowerCase();
  if (lower === 'opened' || lower === 'open' || lower === 'failed' || lower === 'critical') return '#ef4444';
  if (lower === 'resolved' || lower === 'success' || lower === 'successful') return '#10b981';
  if (lower === 'acknowledged' || lower === 'ack' || lower === 'warning') return '#f59e0b';
  if (lower === 'custom') return '#38bdf8';
  if (lower === 'default') return '#a855f7';
  if (lower === 'info') return '#3b82f6';
  return COLOR_PALETTE[index % COLOR_PALETTE.length];
};

const toUTCStartOfDay = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const localDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 0, 0, 0, 0);
      return localDate.toISOString();
    }
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  } catch {
    return dateStr;
  }
};

const toUTCEndOfDay = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const localDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 23, 59, 59, 999);
      return localDate.toISOString();
    }
    const d = new Date(dateStr);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  } catch {
    return dateStr;
  }
};

const formatDate = (isoString?: string) => {
  if (!isoString) return 'N/A';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return isoString;
  }
};

const formatBucketLabel = (bucket: string) => {
  if (!bucket) return '';
  try {
    if (bucket.includes('T')) {
      const d = new Date(bucket);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    const parts = bucket.split('-');
    if (parts.length === 3) {
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  } catch { }
  return bucket;
};

const DeltaText: React.FC<{ deltaPct?: number | null }> = ({ deltaPct }) => {
  if (deltaPct === null || deltaPct === undefined) return null;
  const isPositive = deltaPct > 0;
  const isZero = deltaPct === 0;

  if (isZero) {
    return <span className="text-[11px] text-gray-400 font-medium flex items-center gap-0.5"><Minus className="w-3 h-3" /> 0%</span>;
  }

  return (
    <span className={`text-[11px] font-bold flex items-center gap-0.5 ${isPositive ? 'text-rose-400' : 'text-emerald-400'}`}>
      {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {Math.abs(deltaPct).toFixed(1)}%
    </span>
  );
};

// Custom Tooltip for Area Charts
const CustomChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0f172a] border border-[#1e293b] rounded-xl p-3 shadow-2xl text-xs space-y-1.5 min-w-[140px]">
        <span className="text-[11px] font-bold text-gray-300 block border-b border-[#1e293b] pb-1 font-mono">
          {formatBucketLabel(label)}
        </span>
        {payload.map((item: any, idx: number) => (
          <div key={idx} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-gray-400 capitalize">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color || item.stroke }} />
              {item.name?.replace(/_/g, ' ')}:
            </span>
            <span className="font-bold text-white font-mono">{item.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Custom Tooltip for Leaderboard Line Chart
const CustomLineChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0f172a] border border-[#1e293b] rounded-xl p-3 shadow-2xl text-xs space-y-1 min-w-[140px]">
        <span className="text-[11px] font-bold text-gray-300 block border-b border-[#1e293b] pb-1 font-mono">
          {formatBucketLabel(label)}
        </span>
        <div className="flex items-center justify-between gap-3 pt-1">
          <span className="flex items-center gap-1.5 text-gray-400">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            Occurrences:
          </span>
          <span className="font-bold text-white font-mono">{payload[0].value}</span>
        </div>
      </div>
    );
  }
  return null;
};

// Custom Metric Dropdown with Search
const CustomMetricSelect: React.FC<{
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (val: string) => void;
}> = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOpt = options.find((o) => o.value === value);
  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative inline-block text-left min-w-[160px]">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 bg-[#1a263d] border border-[#273754] rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-200 hover:border-blue-500/60 focus:outline-none transition-all cursor-pointer shadow-sm"
      >
        <span className="truncate">{selectedOpt?.label || value}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180 text-blue-400' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-64 rounded-xl bg-[#0f172a] border border-[#1e293b] shadow-2xl z-50 overflow-hidden py-1.5 animate-in fade-in zoom-in-95 duration-150">
          <div className="px-2 pb-1.5 border-b border-[#1e293b]">
            <input
              type="text"
              placeholder="Search metric..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#131c2e] border border-[#1e293b] rounded-md px-2.5 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              autoFocus
            />
          </div>

          <div className="max-h-56 overflow-y-auto custom-scroll py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400 text-center">No metrics found</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between gap-2 cursor-pointer ${isSelected
                      ? 'bg-blue-600/20 text-blue-400 font-bold'
                      : 'text-gray-300 hover:bg-[#1a263d] hover:text-white'
                      }`}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Item Dropdown with Search (for Leaderboard Items)
const CustomItemSelect: React.FC<{
  value: string;
  options: Array<{ value: string; label: string; count?: number }>;
  onChange: (val: string) => void;
}> = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOpt = options.find((o) => o.value === value) || options[0];
  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative inline-block text-left min-w-[160px] max-w-[210px]">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 bg-[#1a263d] border border-[#273754] rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-200 hover:border-emerald-500/60 focus:outline-none transition-all cursor-pointer shadow-sm"
      >
        <span className="truncate">{selectedOpt ? `${selectedOpt.label}` : 'Select item...'}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180 text-emerald-400' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-72 rounded-xl bg-[#0f172a] border border-[#1e293b] shadow-2xl z-50 overflow-hidden py-1.5 animate-in fade-in zoom-in-95 duration-150">
          <div className="px-2 pb-1.5 border-b border-[#1e293b]">
            <input
              type="text"
              placeholder="Search item..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#131c2e] border border-[#1e293b] rounded-md px-2.5 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500"
              autoFocus
            />
          </div>

          <div className="max-h-56 overflow-y-auto custom-scroll py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400 text-center">No items found</div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between gap-2 cursor-pointer ${isSelected
                      ? 'bg-emerald-600/20 text-emerald-400 font-bold'
                      : 'text-gray-300 hover:bg-[#1a263d] hover:text-white'
                      }`}
                  >
                    <span className="truncate flex-1">{opt.label}</span>
                    {opt.count !== undefined && (
                      <span className="text-[10px] font-mono text-gray-400 bg-[#131c2e] px-1.5 py-0.5 rounded border border-[#1e293b] flex-shrink-0">
                        {opt.count}
                      </span>
                    )}
                    {isSelected && <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Custom Range / Kind Dropdown
const CustomRangeSelect: React.FC<{
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (val: string) => void;
}> = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOpt = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative inline-block text-left min-w-[110px]">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 bg-[#1a263d] border border-[#273754] rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-200 hover:border-blue-500/60 focus:outline-none transition-all cursor-pointer shadow-sm"
      >
        <span className="truncate">{selectedOpt?.label || value}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180 text-blue-400' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-36 rounded-xl bg-[#0f172a] border border-[#1e293b] shadow-2xl z-50 overflow-hidden py-1 animate-in fade-in zoom-in-95 duration-150">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between gap-2 cursor-pointer ${isSelected
                  ? 'bg-blue-600/20 text-blue-400 font-bold'
                  : 'text-gray-300 hover:bg-[#1a263d] hover:text-white'
                  }`}
              >
                <span className="truncate">{opt.label}</span>
                {isSelected && <Check className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Reusable Default Alert Trend Chart Card (Alerts Opened, Resolved, Acknowledged)
const DefaultAlertTrendCard: React.FC<{
  title: string;
  data: TrendMetricData | null;
  loading: boolean;
  color: string;
  gradientId: string;
  icon: React.ReactNode;
  iconBgClass: string;
  totalLabel?: string;
}> = ({ title, data, loading, color, gradientId, icon, iconBgClass, totalLabel }) => {
  const keys = data?.keys && data.keys.length > 0 ? data.keys : ['value'];
  const series = data?.series || [];

  // Determine total value
  let totalCount: number | null = null;
  if (data?.totals) {
    const vals = Object.values(data.totals);
    if (vals.length > 0) {
      totalCount = vals.reduce((a, b) => a + b, 0);
    }
  }
  if (totalCount === null && series.length > 0) {
    const k = keys[0];
    totalCount = series.reduce((sum, item) => sum + (Number(item[k]) || 0), 0);
  }

  return (
    <div className="bg-[#131c2e] border border-[#1e293b] rounded-xl p-4 flex flex-col justify-between shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between gap-2.5 pb-3 border-b border-[#1e293b]">
        <div className="flex items-center gap-2.5">
          <div className={`p-2 rounded-lg border ${iconBgClass}`}>
            {icon}
          </div>
          <div>
            <h3 className="text-xs font-bold text-gray-200 uppercase tracking-wider">{title}</h3>
            <span className="text-[10px] text-gray-400 font-mono">
              {data?.range_label || 'Trend'}
            </span>
          </div>
        </div>

        {totalCount !== null && (
          <div className="text-right">
            <span className="text-base font-black font-mono" style={{ color }}>
              {totalCount.toLocaleString()}
            </span>
            <span className="text-[10px] text-gray-400 block font-medium capitalize">
              {totalLabel || 'total'}
            </span>
          </div>
        )}
      </div>

      {/* Chart Canvas Area */}
      <div className="pt-3 h-44 w-full">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <div className="w-5 h-5 border-2 border-slate-600 border-t-slate-300 rounded-full animate-spin" />
            <span className="text-[10px] text-gray-400 font-mono">Loading trend...</span>
          </div>
        ) : !data || series.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-1 text-center">
            <AlertCircle className="w-5 h-5 text-gray-500" />
            <span className="text-[11px] text-gray-400">No trend data available.</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis
                dataKey="bucket"
                tickFormatter={formatBucketLabel}
                stroke="#64748b"
                tick={{ fill: '#64748b', fontSize: 9 }}
                tickLine={false}
                axisLine={{ stroke: '#1e293b' }}
              />
              <YAxis
                stroke="#64748b"
                tick={{ fill: '#64748b', fontSize: 9 }}
                allowDecimals={false}
                tickLine={false}
                axisLine={{ stroke: '#1e293b' }}
              />
              <Tooltip content={<CustomChartTooltip />} />
              {keys.map((key) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  name={key}
                  stroke={color}
                  strokeWidth={2}
                  fillOpacity={1}
                  fill={`url(#${gradientId})`}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Footer / Range summary */}
      {data && series.length > 0 && (
        <div className="flex items-center justify-between pt-2.5 mt-1 border-t border-[#1e293b] text-[10px]">
          <div className="flex items-center gap-1.5 text-gray-300 font-medium">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            <span className="capitalize">{keys[0]?.replace(/_/g, ' ')}</span>
          </div>
          <span className="text-gray-500 font-mono">
            {data.range_label || 'Period'}
          </span>
        </div>
      )}
    </div>
  );
};

// Reusable Trend Area Chart Component (Left Graph)
const TrendChartCard: React.FC<{
  title: string;
  metric: string;
  onMetricChange: (metric: string) => void;
  data: TrendMetricData | null;
  loading: boolean;
  onRefresh: () => void;
  icon: React.ReactNode;
}> = ({ title, metric, onMetricChange, data, loading, onRefresh, icon }) => {
  const keys = data?.keys || [];
  const series = data?.series || [];

  return (
    <div className="bg-[#131c2e] border border-[#1e293b] rounded-xl p-4 flex flex-col justify-between shadow-sm">
      {/* Header with Metric Dropdown */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 pb-3 border-b border-[#1e293b]">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-[#1a263d] border border-[#273754]">
            {icon}
          </div>
          <div>
            <h3 className="text-xs font-bold text-gray-200 uppercase tracking-wider">{title}</h3>
            <span className="text-[10px] text-gray-400 capitalize">
              {TREND_METRIC_OPTIONS.find((o) => o.value === metric)?.label || metric.replace(/_/g, ' ')}
            </span>
          </div>
        </div>

        {/* Custom Metric Selector Dropdown */}
        <div className="flex items-center gap-1.5 w-full sm:w-auto">
          <CustomMetricSelect
            value={metric}
            options={TREND_METRIC_OPTIONS}
            onChange={onMetricChange}
          />
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="p-2 rounded-lg bg-[#1a263d] border border-[#273754] text-gray-400 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
            title="Refresh metric"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-blue-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Chart Canvas Area */}
      <div className="pt-4 h-64 w-full">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <div className="w-6 h-6 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
            <span className="text-[11px] text-gray-400 font-mono">Loading trend data...</span>
          </div>
        ) : !data || series.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-1.5 text-center">
            <AlertCircle className="w-6 h-6 text-gray-500" />
            <span className="text-xs text-gray-400">No trend series available for this metric.</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                {keys.map((key, idx) => {
                  const color = getKeyColor(key, idx);
                  return (
                    <linearGradient key={key} id={`gradient-${metric}-${key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={color} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={color} stopOpacity={0.0} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis
                dataKey="bucket"
                tickFormatter={formatBucketLabel}
                stroke="#64748b"
                tick={{ fill: '#64748b', fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: '#1e293b' }}
              />
              <YAxis
                stroke="#64748b"
                tick={{ fill: '#64748b', fontSize: 10 }}
                allowDecimals={false}
                tickLine={false}
                axisLine={{ stroke: '#1e293b' }}
              />
              <Tooltip content={<CustomChartTooltip />} />
              {keys.map((key, idx) => {
                const color = getKeyColor(key, idx);
                return (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={key}
                    stroke={color}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill={`url(#gradient-${metric}-${key})`}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Footer / Legend / Totals */}
      {data && keys.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3 mt-2 border-t border-[#1e293b] text-[11px]">
          <div className="flex flex-wrap items-center gap-3">
            {keys.map((key, idx) => {
              const color = getKeyColor(key, idx);
              const totalVal = data.totals ? data.totals[key] : undefined;
              return (
                <span key={key} className="flex items-center gap-1.5 text-gray-300 font-medium">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                  {totalVal !== undefined && (
                    <span className="font-mono text-gray-400 font-bold">({totalVal})</span>
                  )}
                </span>
              );
            })}
          </div>
          <span className="text-[10px] text-gray-500 font-mono">
            {data.range_label || 'Period'}
          </span>
        </div>
      )}
    </div>
  );
};

// Reusable Leaderboard Line Chart Component (Right Graph)
const LeaderboardChartCard: React.FC<{
  kind: string;
  kindOptions: Array<{ value: string; label: string }>;
  onKindChange: (kind: string) => void;
  data: LeaderboardResponseData | null;
  selectedItemId: number | string | null;
  onSelectItem: (id: number | string) => void;
  loading: boolean;
  onRefresh: () => void;
}> = ({ kind, kindOptions, onKindChange, data, selectedItemId, onSelectItem, loading, onRefresh }) => {
  const items = data?.items || [];
  const selectedItem = items.find((item) => String(item.id) === String(selectedItemId)) || items[0];

  const chartData = (selectedItem?.sparkline || []).map((pt) => ({
    bucket: pt.t,
    value: pt.v,
  }));

  const itemOptions = items.map((item) => ({
    value: String(item.id),
    label: item.name,
    count: item.count,
  }));

  return (
    <div className="bg-[#131c2e] border border-[#1e293b] rounded-xl p-4 flex flex-col justify-between shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 pb-3 border-b border-[#1e293b]">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-[#1a263d] border border-[#273754]">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Leaderboard Trends</h3>
            <span className="text-[10px] text-gray-400 truncate max-w-[150px] inline-block">
              {selectedItem ? selectedItem.name : `Top ${kind}`}
            </span>
          </div>
        </div>

        {/* Controls: Kind dropdown + Item dropdown + Refresh */}
        <div className="flex flex-wrap items-center gap-1.5 w-full sm:w-auto">
          <CustomRangeSelect
            value={kind}
            options={kindOptions}
            onChange={(newKind) => onKindChange(newKind)}
          />

          {itemOptions.length > 0 && (
            <CustomItemSelect
              value={String(selectedItem?.id || '')}
              options={itemOptions}
              onChange={(id) => onSelectItem(id)}
            />
          )}

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="p-2 rounded-lg bg-[#1a263d] border border-[#273754] text-gray-400 hover:text-white transition-colors cursor-pointer disabled:opacity-50"
            title="Refresh leaderboard"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-emerald-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Line Chart Canvas */}
      <div className="pt-4 h-64 w-full">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2">
            <div className="w-6 h-6 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
            <span className="text-[11px] text-gray-400 font-mono">Loading leaderboard...</span>
          </div>
        ) : !selectedItem || chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full space-y-1.5 text-center">
            <AlertCircle className="w-6 h-6 text-gray-500" />
            <span className="text-xs text-gray-400">No trend data available for this item.</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis
                dataKey="bucket"
                tickFormatter={formatBucketLabel}
                stroke="#64748b"
                tick={{ fill: '#64748b', fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: '#1e293b' }}
              />
              <YAxis
                stroke="#64748b"
                tick={{ fill: '#64748b', fontSize: 10 }}
                allowDecimals={false}
                tickLine={false}
                axisLine={{ stroke: '#1e293b' }}
              />
              <Tooltip content={<CustomLineChartTooltip />} />
              <Line
                type="monotone"
                dataKey="value"
                name={selectedItem.name}
                stroke="#10b981"
                strokeWidth={2.5}
                dot={{ r: 2.5, fill: '#10b981', stroke: '#131c2e', strokeWidth: 1.5 }}
                activeDot={{ r: 5, fill: '#10b981', stroke: '#ffffff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Footer / Legend / Item Count */}
      {selectedItem && (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-3 mt-2 border-t border-[#1e293b] text-[11px]">
          <div className="flex items-center gap-2 truncate">
            <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
            <span className="font-semibold text-gray-200 truncate">{selectedItem.name}</span>
            <span className="text-emerald-400 font-mono font-bold">({selectedItem.count} total)</span>
          </div>
          <span className="text-[10px] text-gray-500 font-mono">
            {data?.range_label || 'Period'}
          </span>
        </div>
      )}
    </div>
  );
};

export const InsightsTab: React.FC = () => {
  const clientLevel = useClientLevel();
  const currentClientId = clientLevel && clientLevel.name !== 'global' && (clientLevel.id !== undefined || clientLevel.client_id !== undefined)
    ? Number(clientLevel.id !== undefined ? clientLevel.id : clientLevel.client_id)
    : undefined;

  const [selectedRange, setSelectedRange] = useState<string>('last_30_days');
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [data, setData] = useState<TrendsSummaryData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Left Graph State (Area Chart)
  const [leftMetric, setLeftMetric] = useState<string>('alerts_lifecycle');
  const [leftTrends, setLeftTrends] = useState<TrendMetricData | null>(null);
  const [leftTrendsLoading, setLeftTrendsLoading] = useState<boolean>(true);

  // Default 3 Alert Trends (Alerts Opened, Alerts Resolved, Alerts Acknowledged)
  const [openedTrends, setOpenedTrends] = useState<TrendMetricData | null>(null);
  const [resolvedTrends, setResolvedTrends] = useState<TrendMetricData | null>(null);
  const [acknowledgedTrends, setAcknowledgedTrends] = useState<TrendMetricData | null>(null);
  const [alertTrendsLoading, setAlertTrendsLoading] = useState<boolean>(true);

  // Right Graph State (Leaderboard Line Chart)
  const [leaderboardKind, setLeaderboardKind] = useState<string>('rules');
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardResponseData | null>(null);
  const [selectedLeaderboardItemId, setSelectedLeaderboardItemId] = useState<number | string | null>(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState<boolean>(true);

  const getRangeParams = () => {
    const params: any = {
      ...(currentClientId !== undefined ? { client_id: currentClientId } : {})
    };
    if (selectedRange === 'custom') {
      const utcFrom = toUTCStartOfDay(dateFrom);
      const utcTo = toUTCEndOfDay(dateTo);
      return {
        ...params,
        range: 'custom',
        date_from: utcFrom,
        date_to: utcTo,
        from: utcFrom,
        to: utcTo,
      };
    }
    return { ...params, range: selectedRange };
  };

  // Fetch Summary Telemetry
  const fetchTrendsSummary = async (showRefreshingSpinner = false) => {
    if (showRefreshingSpinner) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await automationApi.getTrendsSummary(getRangeParams());
      const resData = response.data as any;
      if (resData && resData.status && resData.data) {
        setData(resData.data);
      } else if (resData && (resData.rules || resData.alerts_now)) {
        setData(resData);
      } else {
        setData(null);
      }
    } catch (err) {
      console.error('Failed to fetch trends summary:', err);
      setData(null);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch Left Trend Graph
  const fetchLeftTrends = async (metricVal: string) => {
    setLeftTrendsLoading(true);
    try {
      const response = await automationApi.getTrends({ metric: metricVal, ...getRangeParams() });
      const resData = response.data as any;
      if (resData && resData.status && resData.data) {
        setLeftTrends(resData.data);
      } else if (resData && resData.series) {
        setLeftTrends(resData);
      } else {
        setLeftTrends(null);
      }
    } catch (err) {
      console.error('Failed to fetch left trends:', err);
      setLeftTrends(null);
    } finally {
      setLeftTrendsLoading(false);
    }
  };

  // Fetch Right Leaderboard Graph
  const fetchLeaderboard = async (kindVal: string) => {
    setLeaderboardLoading(true);
    try {
      const response = await automationApi.getTrendsLeaderboard({ kind: kindVal, ...getRangeParams() });
      const resData = response.data as any;
      if (resData && resData.status && resData.data) {
        setLeaderboardData(resData.data);
        if (resData.data.items && resData.data.items.length > 0) {
          setSelectedLeaderboardItemId(resData.data.items[0].id);
        }
      } else if (resData && resData.items) {
        setLeaderboardData(resData);
        if (resData.items && resData.items.length > 0) {
          setSelectedLeaderboardItemId(resData.items[0].id);
        }
      } else {
        setLeaderboardData(null);
      }
    } catch (err) {
      console.error('Failed to fetch leaderboard trends:', err);
      setLeaderboardData(null);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  // Fetch 3 Default Alert Trends
  const fetchAlertTrends = async () => {
    setAlertTrendsLoading(true);
    try {
      const rangeParams = getRangeParams();
      const [openedRes, resolvedRes, ackRes] = await Promise.all([
        automationApi.getTrends({ metric: 'alerts_opened', ...rangeParams }).catch((err) => {
          console.error('Failed to fetch alerts_opened trends:', err);
          return null;
        }),
        automationApi.getTrends({ metric: 'alerts_resolved', ...rangeParams }).catch((err) => {
          console.error('Failed to fetch alerts_resolved trends:', err);
          return null;
        }),
        automationApi.getTrends({ metric: 'alerts_acknowledged', ...rangeParams }).catch((err) => {
          console.error('Failed to fetch alerts_acknowledged trends:', err);
          return null;
        }),
      ]);

      const extractData = (res: any): TrendMetricData | null => {
        if (!res) return null;
        const resData = res.data;
        if (resData && resData.status && resData.data) return resData.data;
        if (resData && resData.series) return resData;
        return null;
      };

      setOpenedTrends(extractData(openedRes));
      setResolvedTrends(extractData(resolvedRes));
      setAcknowledgedTrends(extractData(ackRes));
    } catch (err) {
      console.error('Failed to fetch default alert trends:', err);
    } finally {
      setAlertTrendsLoading(false);
    }
  };

  const isGlobal = currentClientId === undefined || data?.scope?.level === 'global';

  // Only show 'Clients' option if in Global level
  const availableKindOptions = KIND_OPTIONS.filter((opt) => {
    if (opt.value === 'clients' && !isGlobal) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    if (!isGlobal && leaderboardKind === 'clients') {
      setLeaderboardKind('rules');
    }
  }, [isGlobal, leaderboardKind]);

  useEffect(() => {
    fetchTrendsSummary();
    fetchAlertTrends();
  }, [currentClientId, selectedRange, dateFrom, dateTo]);

  useEffect(() => {
    fetchLeftTrends(leftMetric);
  }, [currentClientId, leftMetric, selectedRange, dateFrom, dateTo]);

  useEffect(() => {
    fetchLeaderboard(leaderboardKind);
  }, [currentClientId, leaderboardKind, selectedRange, dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-2">
        <div className="w-7 h-7 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
        <span className="text-xs text-gray-400 font-mono">Loading insights...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3 bg-[#131c2e] border border-[#1e293b] rounded-xl p-6 text-center">
        <AlertCircle className="w-8 h-8 text-gray-400" />
        <p className="text-xs text-gray-400">No data available for this range.</p>
        <button
          type="button"
          onClick={() => {
            fetchTrendsSummary();
            fetchAlertTrends();
            fetchLeftTrends(leftMetric);
            fetchLeaderboard(leaderboardKind);
          }}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors cursor-pointer"
        >
          Retry
        </button>
      </div>
    );
  }

  const rules = {
    total: data.rules?.total ?? 0,
    active: data.rules?.active ?? 0,
    inactive: data.rules?.inactive ?? 0,
    default: data.rules?.default ?? 0,
    custom: data.rules?.custom ?? 0,
    broken: data.rules?.broken ?? 0,
  };

  const alertsNow = {
    open: data.alerts_now?.open ?? 0,
    acknowledged: data.alerts_now?.acknowledged ?? 0,
    resolved: data.alerts_now?.resolved ?? 0,
  };

  const alertsOpened = {
    current: data.alerts_opened?.current ?? 0,
    previous: data.alerts_opened?.previous ?? 0,
    delta_pct: data.alerts_opened?.delta_pct ?? null,
  };

  const alertsResolved = {
    current: data.alerts_resolved?.current ?? 0,
    previous: data.alerts_resolved?.previous ?? 0,
    delta_pct: data.alerts_resolved?.delta_pct ?? null,
  };

  const actions = {
    current: data.actions?.current ?? 0,
    previous: data.actions?.previous ?? 0,
    failed_current: data.actions?.failed_current ?? 0,
    failed_previous: data.actions?.failed_previous ?? 0,
    success_rate_current: data.actions?.success_rate_current ?? 100,
  };

  // const totalCurrentAlerts = alertsNow.open + alertsNow.acknowledged + alertsNow.resolved;

  return (
    <div className="h-full overflow-y-auto custom-scroll space-y-4 pr-1 pb-16 font-sans">
      {/* Top Header & Range Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-[#131c2e] border border-[#1e293b] px-4 py-3 rounded-xl shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Automation Insights</h2>
            {!isGlobal ? (
              <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-orange-500/10 border border-orange-500/30 text-orange-400 uppercase">
                {clientLevel?.display_name || clientLevel?.name || `Client #${currentClientId}`}
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-slate-800 border border-slate-700 text-slate-400 uppercase">
                Global View
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {formatDate(data.from)} — {formatDate(data.to)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <CustomRangeSelect
            value={selectedRange}
            options={RANGE_OPTIONS}
            onChange={(val) => setSelectedRange(val)}
          />

          {selectedRange === 'custom' && (
            <div className="flex items-center gap-1.5 bg-[#0f172a] border border-[#1e293b] px-2.5 py-1 rounded-lg">
              <div className="flex items-center gap-1">
                <span className="text-[10px] uppercase font-bold text-gray-400 font-mono">From:</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="bg-[#1a263d] border border-[#273754] text-gray-200 text-xs px-2 py-1 rounded-md focus:outline-none focus:border-blue-500 cursor-pointer"
                />
              </div>
              <span className="text-gray-500 font-bold">-</span>
              <div className="flex items-center gap-1">
                <span className="text-[10px] uppercase font-bold text-gray-400 font-mono">To:</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="bg-[#1a263d] border border-[#273754] text-gray-200 text-xs px-2 py-1 rounded-md focus:outline-none focus:border-blue-500 cursor-pointer"
                />
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              fetchTrendsSummary(true);
              fetchAlertTrends();
              fetchLeftTrends(leftMetric);
              fetchLeaderboard(leaderboardKind);
            }}
            disabled={isRefreshing || loading}
            className="p-2 rounded-lg bg-[#1a263d] border border-[#273754] text-gray-300 hover:text-white hover:border-slate-500 transition-colors cursor-pointer disabled:opacity-50"
            title="Refresh All"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-blue-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Top 4 Summary Cards (Dashboard Theme) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">

        {/* Card 1: Rules */}
        <div className="bg-[#131c2e] border border-[#1e293b] rounded-xl p-3.5 flex flex-col justify-between shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#1a263d] border border-[#273754] text-blue-400">
                <Cpu className="w-4 h-4" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">RULES</span>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-2xl font-black text-white">{rules.active}</span>
                  <span className="text-xs text-gray-400 font-medium">/ {rules.total}</span>
                </div>
              </div>
            </div>

            {/* Right Status Badges */}
            <div className="flex flex-col gap-1 items-end">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-emerald-950/40 border border-emerald-800/40 text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                {rules.active} ACTIVE
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-slate-900 border border-slate-700/60 text-gray-300">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                {rules.inactive} INACTIVE
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-gray-400 mt-3 pt-2.5 border-t border-[#1e293b]">
            <span className="flex items-center gap-1 text-gray-300">
              <FileCode2 className="w-3 h-3 text-sky-400" />
              Custom: <strong className="text-white font-mono">{rules.custom}</strong>
            </span>
            <span className="flex items-center gap-1 text-gray-300">
              <Layers className="w-3 h-3 text-purple-400" />
              Default: <strong className="text-white font-mono">{rules.default}</strong>
            </span>
            <span className={`flex items-center gap-1 ${rules.broken > 0 ? 'text-rose-400 font-bold' : 'text-gray-400'}`}>
              <XCircle className="w-3 h-3 text-rose-400" />
              Broken: <strong className="font-mono">{rules.broken}</strong>
            </span>
          </div>
        </div>

        {/* Card 2: Active Alerts */}
        <div className="bg-[#131c2e] border border-[#1e293b] rounded-xl p-3.5 flex flex-col justify-between shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-rose-950/30 border border-rose-800/40 text-rose-400">
                <ShieldAlert className="w-4 h-4" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">ACTIVE ALERTS</span>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-2xl font-black text-rose-400">{alertsNow.open}</span>
                  <span className="text-xs text-gray-400 font-medium">open</span>
                </div>
              </div>
            </div>

            {/* Right Status Badges */}
            <div className="flex flex-col gap-1 items-end">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-rose-950/40 border border-rose-800/40 text-rose-400">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                {alertsNow.open} OPEN
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-emerald-950/40 border border-emerald-800/40 text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                {alertsNow.resolved} RESOLVED
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-gray-400 mt-3 pt-2.5 border-t border-[#1e293b]">
            <span className="flex items-center gap-1 text-gray-300">
              <AlertCircle className="w-3 h-3 text-rose-400" />
              Open: <strong className="text-rose-400 font-mono">{alertsNow.open}</strong>
            </span>
            <span className="flex items-center gap-1 text-gray-300">
              <Clock className="w-3 h-3 text-amber-400" />
              Ack: <strong className="text-amber-400 font-mono">{alertsNow.acknowledged}</strong>
            </span>
            <span className="flex items-center gap-1 text-gray-300">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              Resolved: <strong className="text-emerald-400 font-mono">{alertsNow.resolved}</strong>
            </span>
          </div>
        </div>

        {/* Card 3: Period Alerts */}
        <div className="bg-[#131c2e] border border-[#1e293b] rounded-xl p-3.5 flex flex-col justify-between shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-950/30 border border-amber-800/40 text-amber-400">
                <TrendingUp className="w-4 h-4" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">ALERTS IN PERIOD</span>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-2xl font-black text-white">{alertsOpened.current}</span>
                  <span className="text-xs text-gray-400 font-medium">opened</span>
                </div>
              </div>
            </div>

            {/* Right Status Badges */}
            <div className="flex flex-col gap-1 items-end">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-amber-950/40 border border-amber-800/40 text-amber-400">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                {alertsOpened.current} OPENED
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-slate-900 border border-slate-700/60 text-gray-300">
                <DeltaText deltaPct={alertsOpened.delta_pct} />
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-gray-400 mt-3 pt-2.5 border-t border-[#1e293b]">
            <span className="flex items-center gap-1 text-gray-300">
              <TrendingUp className="w-3 h-3 text-amber-400" />
              Opened: <strong className="text-white font-mono">{alertsOpened.current}</strong>
            </span>
            <span className="flex items-center gap-1 text-gray-300">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              Resolved: <strong className="text-emerald-400 font-mono">{alertsResolved.current}</strong>
            </span>
            <span className="flex items-center gap-1 text-gray-300">
              Res. Rate: <strong className="text-emerald-400 font-mono">{alertsOpened.current > 0 ? `${Math.round((alertsResolved.current / alertsOpened.current) * 100)}%` : '100%'}</strong>
            </span>
          </div>
        </div>

        {/* Card 4: Actions */}
        <div className="bg-[#131c2e] border border-[#1e293b] rounded-xl p-3.5 flex flex-col justify-between shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-950/30 border border-emerald-800/40 text-emerald-400">
                <Zap className="w-4 h-4" />
              </div>
              <div>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">ACTIONS</span>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-2xl font-black text-emerald-400">{actions.success_rate_current}%</span>
                  <span className="text-xs text-gray-400 font-medium">rate</span>
                </div>
              </div>
            </div>

            {/* Right Status Badges */}
            <div className="flex flex-col gap-1 items-end">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-emerald-950/40 border border-emerald-800/40 text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                {actions.current} TOTAL
              </span>
              <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold font-mono px-2 py-0.5 rounded ${actions.failed_current > 0
                ? 'bg-rose-950/40 border border-rose-800/40 text-rose-400'
                : 'bg-slate-900 border border-slate-700/60 text-gray-300'
                }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${actions.failed_current > 0 ? 'bg-rose-400' : 'bg-gray-400'}`}></span>
                {actions.failed_current} FAILED
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-gray-400 mt-3 pt-2.5 border-t border-[#1e293b]">
            <span className="flex items-center gap-1 text-gray-300">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              Succeeded: <strong className="text-emerald-400 font-mono">{actions.current - actions.failed_current}</strong>
            </span>
            <span className={`flex items-center gap-1 ${actions.failed_current > 0 ? 'text-rose-400' : 'text-gray-400'}`}>
              <XCircle className="w-3 h-3 text-rose-400" />
              Failed: <strong className="font-mono">{actions.failed_current}</strong>
            </span>
            <span className="flex items-center gap-1 text-gray-300">
              Rate: <strong className="text-emerald-400 font-mono">{actions.success_rate_current}%</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Default Alert Trends: Alerts Opened, Alerts Resolved, Alerts Acknowledged */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Alerts Opened */}
        <DefaultAlertTrendCard
          title="Alerts Opened"
          data={openedTrends}
          loading={alertTrendsLoading}
          color="#ef4444"
          gradientId="gradient-alerts-opened"
          icon={<ShieldAlert className="w-4 h-4 text-rose-400" />}
          iconBgClass="bg-rose-950/30 border-rose-800/40"
          totalLabel="opened"
        />

        {/* Alerts Resolved */}
        <DefaultAlertTrendCard
          title="Alerts Resolved"
          data={resolvedTrends}
          loading={alertTrendsLoading}
          color="#10b981"
          gradientId="gradient-alerts-resolved"
          icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />}
          iconBgClass="bg-emerald-950/30 border-emerald-800/40"
          totalLabel="resolved"
        />

        {/* Alerts Acknowledged */}
        <DefaultAlertTrendCard
          title="Alerts Acknowledged"
          data={acknowledgedTrends}
          loading={alertTrendsLoading}
          color="#f59e0b"
          gradientId="gradient-alerts-acknowledged"
          icon={<Clock className="w-4 h-4 text-amber-400" />}
          iconBgClass="bg-amber-950/30 border-amber-800/40"
          totalLabel="acknowledged"
        />
      </div>

      {/* Two Graphs Section: Left (Telemetry Trends Area Chart) & Right (Leaderboard Line Chart) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side Area Chart */}
        <TrendChartCard
          title="Telemetry Trends"
          metric={leftMetric}
          onMetricChange={setLeftMetric}
          data={leftTrends}
          loading={leftTrendsLoading}
          onRefresh={() => fetchLeftTrends(leftMetric)}
          icon={<Activity className="w-4 h-4 text-blue-400" />}
        />

        {/* Right Side Line Chart (Leaderboard) */}
        <LeaderboardChartCard
          kind={leaderboardKind}
          kindOptions={availableKindOptions}
          onKindChange={setLeaderboardKind}
          data={leaderboardData}
          selectedItemId={selectedLeaderboardItemId}
          onSelectItem={setSelectedLeaderboardItemId}
          loading={leaderboardLoading}
          onRefresh={() => fetchLeaderboard(leaderboardKind)}
        />
      </div>

    </div>
  );
};
